import Phaser from 'phaser';
import { Boot } from './scenes/Boot';
import { Preload } from './scenes/Preload';
import { Title } from './scenes/Title';
import { ProfileSelect } from './scenes/ProfileSelect';
import { LevelMap } from './scenes/LevelMap';
import { AnimalSelect } from './scenes/AnimalSelect';
import { Game } from './scenes/Game';
import { Results } from './scenes/Results';
import { Settings } from './scenes/Settings';
import { ParentStats } from './scenes/ParentStats';
import { TideStart } from './scenes/TideStart';
import { Helpers } from './scenes/Helpers';
import { Grade } from './scenes/Grade';
import { TideSummary } from './scenes/TideSummary';
import { installDevErrorOverlay } from './devErrorOverlay';
import { installSharpText } from './art/sharpText';
import { installHiDpiRenderer } from './art/hiDpiRenderer';
import { installKeyboardOnce } from './systems/keyboardOnce';
import { installTouchKeyboard, isTouchDevice } from './systems/touchKeyboard';
import { setAnimalArtStyle } from './data/animals';
import { devArtStyle } from './systems/devQuery';
import { GAME_HEIGHT, GAME_WIDTH } from './types';

/**
 * Wait for the bundled Atkinson Hyperlegible before the first canvas text is
 * measured, otherwise Phaser bakes glyph metrics from the fallback font.
 */
async function ensureFontsReady(timeoutMs = 2500): Promise<void> {
  if (document.fonts === undefined) return;
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  const loaded = (async (): Promise<void> => {
    await Promise.all([
      document.fonts.load('400 48px "Atkinson Hyperlegible"'),
      document.fonts.load('700 48px "Atkinson Hyperlegible"'),
    ]);
    await document.fonts.ready;
  })();
  try {
    await Promise.race([loaded, timeout]);
  } catch {
    console.warn('[main] Font preload failed; continuing with fallback font.');
  }
}

/** Keys the browser would otherwise act on: scroll, step focus, or go back. */
const PAGE_KEYS = new Set([
  ' ',
  'Backspace',
  'Tab',
  'Enter',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

/**
 * Keep those keys inside the game on every screen (CLAUDE.md).
 *
 * TypingInput captures them during a level, but the menus only listen, so on
 * the title or the map Tab stepped focus out of the canvas and the arrows
 * could scroll. One listener covers every scene.
 *
 * It must run after Phaser's own window listener, never before: Phaser drops
 * any key event that is already defaultPrevented, so blocking it first would
 * swallow Space and Enter. Hence registered on READY, once Phaser's is in.
 */
function keepKeysInGame(game: Phaser.Game): void {
  game.events.once(Phaser.Core.Events.READY, () => {
    window.addEventListener('keydown', (event) => {
      if (!PAGE_KEYS.has(event.key)) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable === true || target instanceof HTMLInputElement) return;
      if (target instanceof HTMLTextAreaElement) return;
      event.preventDefault();
    });
  });
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: '#0b1220',
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  scale: {
    mode: Phaser.Scale.FIT,
    // Phones: pinned to the top, above where the on-screen keyboard opens.
    autoCenter: isTouchDevice() ? Phaser.Scale.CENTER_HORIZONTALLY : Phaser.Scale.CENTER_BOTH,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
  },
  render: {
    pixelArt: false,
    antialias: true,
    roundPixels: false,
  },
  // Pause when the tab/window loses focus (CLAUDE.md).
  autoFocus: true,
  disableContextMenu: true,
  scene: [
    Boot,
    Preload,
    Title,
    ProfileSelect,
    LevelMap,
    AnimalSelect,
    Game,
    Results,
    Settings,
    ParentStats,
    TideStart,
    TideSummary,
    Helpers,
    Grade,
  ],
};

installDevErrorOverlay();
installSharpText();
installKeyboardOnce();
setAnimalArtStyle(devArtStyle());
await ensureFontsReady();
document.getElementById('boot-note')?.remove();
export const game = new Phaser.Game(config);
// Draw at the screen's real pixel count before the first frame (hiDpiRenderer.ts).
game.events.once(Phaser.Core.Events.READY, () => installHiDpiRenderer(game));
keepKeysInGame(game);
const host = document.getElementById('game');
if (host !== null) installTouchKeyboard(host);

// Dev only: lets the Playwright harnesses read real scene state (positions,
// bounds, scene keys) instead of inferring it from pixels in a screenshot.
if (import.meta.env.DEV) {
  (window as unknown as { game: Phaser.Game }).game = game;
}
